import React, { useEffect, useState, useRef } from "react";
import { getActiveStep, selectActiveLayer, setActiveLayer } from "../../../../../../../redux/toolsSlice";
import { getLayerObjConfig, removeLayerConfig, setLayerConfig, updateLayer } from '../../../../../../../redux/stepsSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getMaterial } from "../../../../../../../redux/materialSlice";
import { getResponseData } from "../../../../../../../redux/hubSlice";
import {
  buildResponsePayload,
  getAttemptByLayerId,
  getCurrentTime,
  isHubRuntime,
  saveHubResponse
} from "./hubResponseUtils";

export const MultiSelectLayerDefaults = (id) => {
  return {
    id: id,
    type: 'multi_select',
    question: 'Enter your question',
    multiSelect: [
      { id: uuidv4(), value: "", isChecked: false },
    ],
  };
}

function MultiSelectContent({ layer }) {

  const [isEditing, setIsEditing] = useState(false);
  const [question, setQuestion] = useState(layer.question);
  const [options, setOptions] = useState(layer.multiSelect || []);

  const questionRef = useRef(null);
  const dispatch = useDispatch();

  const layerObjConfig = useSelector((state) => getLayerObjConfig(state, layer), shallowEqual);
  const activeStep = useSelector((state) => getActiveStep(state));
  const stepId = activeStep.id;
  const activeLayer = useSelector((state) => selectActiveLayer(state));

  const defaultConfig = [
    {
      layerId: layer.id,
      sectionId: 'multiSelect',
      sectionTitle: 'Multi Select Element',
      values: [
        {
          id: 'question',
          title: 'Question',
          type: 'text',
          value: layer.question || ''
        },
        {
          id: 'multiSelect',
          title: '',
          type: 'multiSelect',
          value: layer.multiSelect,
          // options: options,
          direction: 'column'
        },
      ]
    }
  ];

  const EditElement = () => {
    removeFocusElement();
    dispatch(setActiveLayer(layer));
    dispatch(setLayerConfig(defaultConfig));
    setIsEditing(true);
  }

  const removeFocusElement = () => {
    dispatch(setActiveLayer({}));
    dispatch(removeLayerConfig());
    setIsEditing(false);
  }

  const updateText = () => {
    const newLayer = {
      ...layer,
      question: questionRef.current ? questionRef.current.value : layer.question,
      multiSelect: layer.multiSelect ? layer.multiSelect : []
    };
    dispatch(updateLayer({ stepId: stepId, layer: newLayer }));
  };

  useEffect(() => {
    if (layerObjConfig) {
      const newLayer = { ...layer, ...layerObjConfig };
      dispatch(updateLayer({ stepId: stepId, layer: newLayer }));
    }
  }, [layerObjConfig]);

  useEffect(() => {
    setQuestion(layer.question);
  }, [layer]);

  useEffect(() => {
    setOptions(layer.multiSelect || []);
  }, [layer]);

  const handleEditBlur = (e) => {
    removeFocusElement();
    setIsEditing(false);
    updateText();
  };

  const onPressEnter = (e) => {
    if (e.key === 'Enter' || e.key === "Escape") {
      removeFocusElement();
      setIsEditing(false);
      updateText();
    }
  };

  return (
    <>
      <div className="container-large-text" onBlur={removeFocusElement} onKeyDown={onPressEnter} onClick={EditElement}>
        {isEditing ? (
          <>
            <input
              className="mr-2"
              ref={questionRef}
              defaultValue={layer.question}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onBlur={handleEditBlur}
              onKeyDown={onPressEnter}
              style={{
                border: 'none',
                padding: 0,
                margin: 0,
                width: '100%',
                height: '100%',
                fontWeight: '400',
                fontSize: '15px'
              }}
              placeholder="Enter your question"
            />
            {options.map((data) => {
              return (
                <label key={data.id} className="un-choice-option">
                  <input
                    className="dropdown-data-checkbox"
                    type="checkbox"
                    value={data.isChecked}
                    readOnly
                  />
                  <span className="un-choice-option__label">{data.value || 'Option'}</span>
                </label>
              )
            })
            }
          </>
        ) : (
          <MultiSelectContentPreview layer={layer} EditElement={EditElement} />
        )}
      </div >
    </>
  )
}

export const MultiSelectContentPreview = ({ layer, EditElement }) => {
  const dispatch = useDispatch();
  const material = useSelector((state) => getMaterial(state));
  const activeStep = useSelector((state) => getActiveStep(state));
  const responseData = useSelector((state) => getResponseData(state));
  const [selectedOptions, setSelectedOptions] = useState([]);
  const audience = isHubRuntime();

  useEffect(() => {
    const existingAttempt = getAttemptByLayerId(responseData, activeStep?.id, layer?.id);
    if (Array.isArray(existingAttempt?.answer)) {
      setSelectedOptions(existingAttempt.answer);
      return;
    }
    if (typeof existingAttempt?.answer === 'string') {
      setSelectedOptions(existingAttempt.answer.split(',').map((item) => item.trim()).filter(Boolean));
      return;
    }
    setSelectedOptions([]);
  }, [responseData, activeStep?.id, layer?.id]);

  const updateSelection = async (optionValue, checked) => {
    const nextSelections = checked
      ? Array.from(new Set([...selectedOptions, optionValue]))
      : selectedOptions.filter((value) => value !== optionValue);

    setSelectedOptions(nextSelections);

    if (!audience || !material?.id || !activeStep?.id) return;

    const attemptDetail = {
      id: layer?.id,
      answer: nextSelections,
      question: {
        type: 'multi_select',
        description: layer?.question
      },
      created_at: getCurrentTime()
    };

    const payload = buildResponsePayload({
      responseData,
      activeStepId: activeStep.id,
      attemptDetail
    });

    try {
      await saveHubResponse({
        materialId: material.id,
        payload,
        dispatch,
        responseData
      });
    } catch (error) {
      console.error("Error saving multi select response:", error);
    }
  };

  return (
    <>
      <p
        tabIndex={0}
        onClick={!audience ? EditElement : undefined}
        style={{
          fontWeight: '400',
          fontSize: '15px'
        }}
      >
        {layer.question}
      </p>
      {layer.multiSelect && layer.multiSelect.map((data) => {
        const checked = selectedOptions.includes(data.value);
        return (
          <label key={`${layer.id}_${data.id}`} className="un-choice-option">
            <input
              className="dropdown-data-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => updateSelection(data.value, e.target.checked)}
            />
            <span className="un-choice-option__label">{data.value}</span>
          </label>
        )
      })
      }
    </>
  )
}

export default MultiSelectContent;
